import {
  Box,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Pagination,
  PaginationItem,
  Select,
  Stack,
  Typography,
  Paper,
  FormControl,
  Tooltip,
  useTheme,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloudIcon from "@mui/icons-material/Cloud";
import React, { useCallback, useEffect, useState } from "react";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import InfraDetailsDialog from "./InfraListDialogs/infraDetailsDialog";
import { INFRA_VIEWS } from "../../../../apis/organization-apis/Infra-apis/InfraApis";
import { useMutation } from "@apollo/client";
import { OverviewTableListHeadings } from "../../../../data/data";
import TruncatableTableCell from "../../../../components/ReusableTable/TrucatableTableCell";
import { CheckStatusChip } from "../../../../components/CheckStatusChip/CheckStatusChip";
import useAppStore from "../../../../stores";

// AWS service categories
const awsServiceCategories = {
  Compute: ["EC2", "AWSLAMBDA"],
  Storage: ["S3", "EFS"],
  Database: ["DYNAMODB", "RDS"],
  Networking: ["VPC", "ROUTE53", "APIGATEWAY", "ELBV2"],
  Security: ["IAM", "ACM", "GUARDDUTY", "SECURITYHUB", "WAFV2"],
  Monitoring: ["CLOUDWATCH", "CLOUDTRAIL", "CONFIG"],
  "Content Delivery": ["CLOUDFRONT"],
  "AI/ML": ["BEDROCK"],
};

// Helper function to categorize service
const categorizeService = (entityType) => {
  const upperEntityType = entityType?.toUpperCase();

  for (const [category, services] of Object.entries(awsServiceCategories)) {
    if (services.includes(upperEntityType)) {
      return category;
    }
  }

  return "Other Services";
};

const AWSInfraList = () => {
  const theme = useTheme();
  const [generateInfraViews, { loading }] = useMutation(INFRA_VIEWS);
  const [infraListData, setInfraListData] = useState([]);
  const [selectedEntity_type, setSelectedEntity_type] = useState("acm");
  const [selectedEntity_name, setSelectedEntity_Name] = useState("acm");
  const [selectedInfraData, setSelectedInfraData] = useState([]);
  const [infraTypes, setInfraTypes] = useState([]);
  const [loaderType, setLoaderType] = useState("");
  const [filter, setFilter] = useState({});
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const selectedFrameworkId = useAppStore((state) => state.selectedFrameworkId);
  const handleData = useCallback((res) => {
    let infraListDetails =
      res?.data.fetchInfraViews?.data.length > 0 &&
      res?.data.fetchInfraViews?.data.map((item) => {
        return {
          all_entity: item.entity_name,
          check_id: item.check_id ? item.check_id : "-",
          resource_id: item.resource_id,
          resource_name: item.resource_name === "" ? "-" : item.resource_name,
          check_name: item.check_name,
          check_description: item.check_description,
          status: item.status,
          statusReason: item.status_reason,
        };
      });
    let allInfaTypes = res?.data.fetchInfraViews?.metadata?.group
      ? res?.data.fetchInfraViews?.metadata
      : null;
    setInfraTypes(allInfaTypes);
    let infraDetails = infraListDetails ? infraListDetails : null;
    setInfraListData(infraDetails);
  }, []);

  useEffect(() => {
    async function getInfraListData() {
      try {
        let res = await generateInfraViews({
          variables: {
            page: page,
            limit: Number(limit),
            entity_type: selectedEntity_type,
            integrationKey: "aws",
            framework_id: selectedFrameworkId,
            ...filter,
          },
        });
        if (res) {
          handleData(res);
        }
      } catch (error) {
        console.log(error);
      }
    }
    getInfraListData();
  }, [
    filter,
    generateInfraViews,
    handleData,
    limit,
    page,
    selectedEntity_type,
    selectedFrameworkId,
  ]);

  const handleOpenDialog = (infra) => {
    setSelectedInfraData(infra);
    setOpen(true);
  };

  const handleInfraType = (entity_name, entity_Type) => {
    setSelectedEntity_Name(entity_name);
    setSelectedEntity_type(entity_Type);
    setPage(1);
    setLoaderType("panel loader");
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const getImage = (logo) => {
    try {
      return require(`../../../../Assets/Images/${logo}.png`);
    } catch (error) {
      console.error(`Error loading image: ${logo}`, error);
    }
  };

  const renderModifiedEntity = (entityType) => {
    const formattedEntityType = entityType
      .split("_")
      .map((word) =>
        word?.toUpperCase() === "AWS" ? "AWS" : word?.toUpperCase()
      )
      .join(" ");
    return formattedEntityType;
  };

  const getFilteredServices = () => {
    if (!infraTypes?.group) return [];

    return infraTypes.group.filter((item) =>
      renderModifiedEntity(item.entity_type)
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  };

  const groupServicesByCategory = () => {
    const filteredServices = getFilteredServices();
    const groupedServices = {};

    filteredServices.forEach((service) => {
      const entityName = renderModifiedEntity(service.entity_type);
      const category = categorizeService(service.entity_type);

      if (!groupedServices[category]) {
        groupedServices[category] = [];
      }

      groupedServices[category].push(service);
    });

    return groupedServices;
  };

  const renderCell = (row, column) => {
    switch (column.field) {
      case "description":
        return (
          <Typography variant="body2" fontWeight={400} color="text.primary">
            {row.description}
          </Typography>
        );
      case "resource_id":
        return (
          <div>
            <TruncatableTableCell
              value={row.resource_id}
              maxPreviewLength={50}
              startLength={20}
              endLength={60}
            />
          </div>
        );
      case "check_name":
        return (
          <Typography variant="body2" fontWeight={400} color="text.primary">
            {row.check_name}
          </Typography>
        );
      case "status":
        return (
          <Tooltip title={row.statusReason} arrow>
            <span>
              <CheckStatusChip status={row.status} />
            </span>
          </Tooltip>
        );
      case "action":
        return (
          <IconButton
            onClick={() => handleOpenDialog(row)}
            size="small"
            sx={{ color: "#031B89" }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        );
      case "status_reason":
        return (
          <Typography variant="body2" fontWeight={400} color="text.primary">
            {row.status_reason}
          </Typography>
        );
      default:
        return row[column.field];
    }
  };

  const groupedServices = groupServicesByCategory();
  const onFilterChange = (filter) => {
    setFilter(filter);
  };

  const renderRowFooter = (row, rowIndex) => {
    if (row.status == "pass" || !row.statusReason) return null;

    return (
      <Box
        sx={{
          padding: "0px 16px 4px 16px",
          backgroundColor: "transparent",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: "0.75rem",
            textAlign: "right",
            justifyContent: "flex-end",
            display: "flex",
            alignItems: "center",
            fontStyle: "italic",
          }}
        >
          Reason: {row.statusReason}
        </Typography>
      </Box>
    );
  };

  return (
    <div>
      <Box>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight="700">
            {renderModifiedEntity(selectedEntity_type)}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item lg={2.5}>
            <Paper
              elevation={1}
              sx={{
                height: "70vh",
                border: `1px solid ${theme.customColors.border.strong}`,
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "16px",
                  backgroundColor: theme.customColors.background.header,
                  borderBottom: `1px solid ${theme.customColors.border.strong}`,
                }}
              >
                <Box sx={{ mr: 1 }}>
                  {getImage("aws") ? (
                    <CardMedia
                      component="img"
                      image={getImage("aws")}
                      alt="AWS"
                      sx={{
                        height: 24,
                        width: 24,
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <CloudIcon sx={{ color: "#FF9900", fontSize: 24 }} />
                  )}
                </Box>
                <Typography variant="subtitle1" fontWeight="700">
                  AWS
                </Typography>
              </Box>

              <Box sx={{ height: "calc(100% - 56px)", overflowY: "auto" }}>
                {searchQuery ? (
                  <></>
                ) : (
                  Object.entries(groupedServices).map(
                    ([category, services]) => (
                      <Box key={category}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1,
                            pl: 2,
                            backgroundColor:
                              theme.customColors.background.tableRow,
                            borderTop: `1px solid ${theme.customColors.border.strong}`,
                            borderBottom: `1px solid ${theme.customColors.border.strong}`,
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color="text.secondary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            {category} ({services.length})
                          </Typography>
                        </Box>
                        <List sx={{ p: 0 }}>
                          {services.map((item, i) => (
                            <ListItem
                              key={i}
                              sx={{
                                display: "flex",
                                cursor: "pointer",
                                borderLeft:
                                  item.entity_type === selectedEntity_type
                                    ? `4px solid ${theme.palette.primary.main}`
                                    : "4px solid transparent",
                                backgroundColor:
                                  item.entity_type === selectedEntity_type
                                    ? "rgba(3, 27, 137, 0.05)"
                                    : "transparent",
                                "&:hover": {
                                  backgroundColor: "rgba(3, 27, 137, 0.04)",
                                },
                              }}
                              onClick={() =>
                                handleInfraType(
                                  item.entity_name,
                                  item.entity_type
                                )
                              }
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight:
                                    item.entity_type === selectedEntity_type
                                      ? 600
                                      : 400,
                                }}
                              >
                                {renderModifiedEntity(item.entity_type)}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )
                  )
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item lg={9.5}>
            <ReusableTable
              columns={OverviewTableListHeadings}
              data={infraListData}
              renderCell={renderCell}
              renderRowFooter={renderRowFooter}
              showPagination={false}
              onFilterChange={onFilterChange}
              filters={filter}
              loading={loading}
            />
            {!loading && (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ padding: 2 }}
              >
                <FormControl variant="outlined" size="small">
                  <Select value={limit} onChange={handleLimitChange}>
                    {[10, 20, 40, 80].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Pagination
                  count={infraTypes?.totalPages || 1}
                  page={page}
                  onChange={handlePageChange}
                  variant="outlined"
                  shape="rounded"
                  renderItem={(item) => (
                    <PaginationItem
                      {...item}
                      sx={{
                        background: item.selected ? "#031B89 !important" : "",
                        color: item.selected ? "#fff !important" : "",
                      }}
                    />
                  )}
                />
              </Stack>
            )}
          </Grid>
        </Grid>
      </Box>
      {selectedInfraData && (
        <InfraDetailsDialog
          open={open && selectedInfraData}
          onClose={() => setOpen(false)}
          selectedInfraData={selectedInfraData}
        />
      )}
    </div>
  );
};

export default AWSInfraList;
